import { GridColDef } from '@mui/x-data-grid';
import { GridColumnVisibilityModel } from '@mui/x-data-grid/hooks/features/columns/gridColumnsInterfaces';
import React from 'react';
import { Link } from 'react-router-dom';

import DataGrid from '#app/Component/DataGrid';
import { getRoutePathname } from '#app/Handler/RouteHandler';
import { transformObjectToDataGridRowsArray } from '#app/Util/DataGridDataTransformer';
import { momentDateFormatter } from '#app/Util/MomentDateFormatter';
import { formatNumberToEurCurrency } from '#app/Util/NumberTool';
import { Filters } from '#app/View/Statistic/Order/StatisticOrder';
import { useCollectionPaginated } from '#app/hooks/useCollectionPaginated';
import {
  OrderDocumentStats,
  OrderDocumentType,
  OrderDocumentTypeTranslation,
  TaxTypeId,
  TaxTypeTranslation,
} from '#app/typings/orderDocument';
import { PaymentMethod, PaymentMethodTranslation } from '#app/typings/orderPayment';

type Props = Omit<ReturnType<typeof useCollectionPaginated<OrderDocumentStats, Filters>>, 'setFilters' | 'filters'>;

const List: React.FC<Props> = ({
  data: orderDocuments,
  totalItems,
  loading,
  pagination,
  sorting,
  setPagination,
  setSorting,
}) => {
  const getTaxValueColumns = (): GridColDef[] =>
    (Object.keys(TaxTypeTranslation) as TaxTypeId[]).map((id) => ({
      field: `taxBase${id}`,
      headerName: `Base ${TaxTypeTranslation[id]} HT`,
      sortable: false,
      minWidth: 120,
      valueGetter: (_, row: OrderDocumentStats) =>
        row.taxRates[id]?.base ? formatNumberToEurCurrency(row.taxRates[id]?.base) : '-',
    }));

  const getTaxRateColumns = (): GridColDef[] =>
    (Object.keys(TaxTypeTranslation) as TaxTypeId[]).map((id) => ({
      field: `taxTotal${id}`,
      headerName: `TVA ${TaxTypeTranslation[id]}`,
      sortable: false,
      valueGetter: (_, row: OrderDocumentStats) =>
        row.taxRates[id]?.total ? formatNumberToEurCurrency(row.taxRates[id]?.total) : '-',
    }));

  const getColumnVisibilityModelFromTaxType = (): GridColumnVisibilityModel =>
    Object.keys(TaxTypeTranslation).reduce((acc, taxId) => {
      const hidden = !orderDocuments.every((orderDocument) => orderDocument.taxRates[taxId as TaxTypeId] === undefined);
      acc[`taxBase${taxId}`] = hidden;
      acc[`taxTotal${taxId}`] = hidden;
      return acc;
    }, {} as GridColumnVisibilityModel);

  const columns: GridColDef[] = [
    {
      field: 'date',
      headerName: 'Date',
      valueGetter: (value: string) => momentDateFormatter.dateTimeToFormatFr(value),
    },
    {
      field: 'type',
      headerName: 'Type',
      sortable: false,
      minWidth: 110,
      valueGetter: (value: OrderDocumentType) => OrderDocumentTypeTranslation[value],
    },
    {
      field: 'reference',
      headerName: 'Référence',
      flex: 1,
      minWidth: 200,
      sortable: false,
      renderCell: ({ value, row }) => (
        <Link to={getRoutePathname('sale_order_client', { uuid: row.orderClientUuid })}>{value}</Link>
      ),
    },
    {
      field: 'paymentMethod',
      headerName: 'Methode',
      sortable: false,
      valueGetter: (value: PaymentMethod) => PaymentMethodTranslation[value],
    },
    {
      field: 'totalHT',
      headerName: 'Total HT',
      valueGetter: (value) => formatNumberToEurCurrency(value),
    },
    {
      field: 'totalTVA',
      headerName: 'Total TVA',
      sortable: false,
      valueGetter: (value) => formatNumberToEurCurrency(value),
    },
    {
      field: 'totalProductsHT',
      headerName: 'Total Produits HT',
      minWidth: 140,
      sortable: false,
      valueGetter: (value) => formatNumberToEurCurrency(value),
    },
    {
      field: 'totalShippingFees',
      headerName: 'Frais de port HT',
      minWidth: 135,
      sortable: false,
      valueGetter: (value) => (value > 0 ? formatNumberToEurCurrency(value) : '-'),
    },
    {
      field: 'totalPaymentFees',
      headerName: 'Frais payment HT',
      minWidth: 140,
      sortable: false,
      valueGetter: (value) => (value > 0 ? formatNumberToEurCurrency(value) : '-'),
    },
    {
      field: 'countryCode',
      headerName: 'Pays',
      sortable: false,
      minWidth: 100,
    },
    ...getTaxValueColumns(),
    ...getTaxRateColumns(),
    {
      field: 'totalTTC',
      headerName: 'Total TTC',
      valueGetter: (value) => formatNumberToEurCurrency(value),
    },
  ];

  return (
    <DataGrid
      rows={transformObjectToDataGridRowsArray<OrderDocumentStats>(orderDocuments)}
      columns={columns}
      loading={loading}
      rowCount={totalItems}
      columnVisibilityModel={getColumnVisibilityModelFromTaxType()}
      setSorting={setSorting}
      setPagination={setPagination}
      pagination={pagination}
      sorting={sorting}
    />
  );
};

export default List;
